import React, { useRef, useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import remarkGfm from 'remark-gfm'
import 'highlight.js/styles/github.css'
import '@/styles/highlight-dark.css'
import { useOpsChatStreaming } from '@/hooks/useOpsChatStreaming'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import useAuthSession from '@/hooks/useAuthSession'
import { OpsMessage, OpsChatSession } from '@/store/slices/opsChatSlice'
import { StreamingOpsMessage } from '@/components/ops/StreamingOpsMessage'
import { OpsConnectionStatus, OpsTypingIndicator } from '@/components/ops/OpsConnectionStatus'
import OpsConversationItem from '@/components/ops/OpsConversationItem'
import { updateOpsChatSession, deleteOpsChatSession } from '@/store/slices/opsChatSlice'
import { VersionIndicator } from '@/components/common/VersionIndicator'
import { ChangelogModal } from '@/components/common/ChangelogModal'
import { useAgentVersions } from '@/hooks/useAgentVersions'
import { StoreMemoryPanel } from '@/components/ops/StoreMemoryPanel'
import { ChevronDown, BarChart3, TrendingUp, Users, DollarSign } from 'lucide-react'

const OpsAgentPage: React.FC = () => {
  // Authentication
  const { user, token } = useAppSelector((state) => state.auth)
  const dispatch = useAppDispatch()
  useAuthSession() // Initialize authentication

  // Debug: Log user info to check role and branchId
  useEffect(() => {
    console.log('🔍 OpsAgentPage - User info:', {
      role: user?.role,
      branchId: user?.branchId,
      showMemoryPanel: user?.role === 'branch_manager' && !!user?.branchId
    })
  }, [user])

  // Get current year for fiscal period examples
  const currentYear = new Date().getFullYear()
  
  // Ops chat streaming hook
  const {
    sessions,
    activeSession,
    activeSessionId,
    isInitialized,
    loadingSessions,
    streamingEnabled,
    hasActiveStreams,
    typingUsers,
    isConnected,
    sendMessage,
    cancelMessage,
    loadSession,
    startNewChat,
    sendTyping,
    connect,
    getSessionState
  } = useOpsChatStreaming()

  // Local state for input (UI state that doesn't need Redux)
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [showChangelog, setShowChangelog] = useState(false)
  
  // Agent versions
  const { versions } = useAgentVersions()
  
  // Debug logging
  useEffect(() => {
    console.log('🏭 Ops Page - versions updated:', versions)
  }, [versions])
  
  // Get current session state
  const { messages, isLoading } = getSessionState()
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout>()
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  
  // State for scroll to bottom button
  const [showScrollButton, setShowScrollButton] = useState(false)

  // Smart scroll: only auto-scroll if user is near bottom
  const scrollToBottom = (force = false) => {
    if (!messagesEndRef.current || !messagesContainerRef.current) return
    
    const container = messagesContainerRef.current
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100
    
    if (force || isNearBottom) {
      // Use scrollTo instead of scrollIntoView to avoid window scrolling
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      })
    }
  }

  useEffect(() => {
    // Only auto-scroll for new messages, not during streaming updates
    const lastMessage = messages[messages.length - 1]
    if (lastMessage && !lastMessage.isStreaming) {
      scrollToBottom(false) // Smart scroll
    }
  }, [messages.length]) // Only trigger on new messages, not content updates

  // Handle scroll events to show/hide scroll button
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const handleScroll = () => {
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100
      setShowScrollButton(!isNearBottom)
    }

    container.addEventListener('scroll', handleScroll)
    // Initial check
    handleScroll()

    return () => container.removeEventListener('scroll', handleScroll)
  }, [activeSessionId, messages.length]) // Re-check when session or messages change

  // Handle message send
  const handleSendMessage = async () => {
    if (!inputValue.trim() || hasActiveStreams) return

    const messageContent = inputValue.trim()
    setInputValue('')
    setIsTyping(false)
    
    try {
      await sendMessage(messageContent, activeSessionId || undefined)
      // Force scroll to bottom after sending a message
      setTimeout(() => {
        scrollToBottom(true)
        inputRef.current?.focus()
      }, 100)
    } catch (error) {
      console.error('Failed to send message:', error)
      // Restore input value on error
      setInputValue(messageContent)
      inputRef.current?.focus()
    }
  }

  // Handle typing indicators
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)

    // Send typing indicator
    if (value.trim() && !isTyping) {
      setIsTyping(true)
      sendTyping(true)
    } else if (!value.trim() && isTyping) {
      setIsTyping(false)
      sendTyping(false)
    }

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }

    // Stop typing after 2 seconds of no input
    if (value.trim()) {
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false)
        sendTyping(false)
      }, 2000)
    }
  }

  // Handle message cancellation
  const handleCancelMessage = (messageId: string) => {
    cancelMessage(messageId)
  }

  // Handle quick action clicks
  const handleQuickAction = (action: string) => {
    // Different prompts for branch managers vs general managers
    const isBranchManager = user?.role === 'branch_manager'

    const branchManagerPrompts = {
      'monday-review': "How did my store do last week?",
      'week-comparison': "Compare my store's sales this week vs last week",
      'department-performance': "Which departments are up and which are down this period?",
      'period-summary': "Give me a summary of my store's current fiscal period so far",
      'add-note': "Remember that we have a new assistant manager starting next week"
    }

    const generalManagerPrompts = {
      'sales-period': `Show me Period 8 ${currentYear} sales summary for all stores`,
      'sales-current': "Show me current fiscal period sales summary for all stores",
      'performance': "Which are our top performing stores this fiscal period?"
    }

    const prompts = isBranchManager ? branchManagerPrompts : generalManagerPrompts
    setInputValue(prompts[action as keyof typeof prompts] || action)
    inputRef.current?.focus()
  }

  // Handle Enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  // Handle session rename
  const handleRenameSession = async (sessionId: string, newTitle: string) => {
    try {
      await dispatch(updateOpsChatSession({ sessionId, updates: { title: newTitle } })).unwrap()
    } catch (error) {
      console.error('Failed to rename session:', error)
      throw error
    }
  }

  // Handle session delete
  const handleDeleteSession = async (sessionId: string) => {
    try {
      await dispatch(deleteOpsChatSession(sessionId)).unwrap()
      // If we deleted the active session, start a new chat
      if (sessionId === activeSessionId) {
        startNewChat()
      }
    } catch (error) {
      console.error('Failed to delete session:', error)
      throw error
    }
  }

  // Cleanup typing timeout
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div className="px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Operations Agent</h1>
          <p className="text-muted-foreground">
            Get insights from store performance data and operational metrics through PowerBI integration
          </p>
        </div>

        {/* Authentication Check - Authorization is handled by ProtectedRoute */}
        {!user || !token ? (
          <div className="bg-card border rounded-lg p-8 text-center">
            <h3 className="text-lg font-semibold mb-2">Authentication Required</h3>
            <p className="text-muted-foreground">Please log in to access the Operations Agent.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Connection Status */}
            <div className="lg:col-span-4">
              <OpsConnectionStatus 
                onReconnect={connect}
                className="mb-4"
              />
            </div>

            {/* Chat Interface */}
            <div className="lg:col-span-3">
              <div className="bg-card border rounded-lg h-[75vh] min-h-[700px] flex flex-col relative">
                {/* Loading Overlay for Session Switching ONLY */}
                {activeSession?.isLoading && (
                  <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
                    <div className="bg-card border rounded-lg p-6 shadow-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <div>
                          <p className="font-medium">Loading conversation...</p>
                          <p className="text-sm text-muted-foreground">Please wait</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Chat Header */}
                <div className="border-b p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold flex items-center gap-2">
                          Operations Assistant
                          {/* Only show Live when WebSocket is connected - consistent with connection status */}
                          {isConnected && (
                            <span className="text-xs bg-green-500/10 text-green-600 dark:bg-green-400/10 dark:text-green-400 px-2 py-1 rounded-full">
                              Live
                            </span>
                          )}
                          {versions.ops && (
                            <VersionIndicator
                              versionInfo={versions.ops}
                              onViewChangelog={() => setShowChangelog(true)}
                              variant="outline"
                              size="sm"
                            />
                          )}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {(() => {
                            if (activeSession?.isLoading) return 'Loading conversation...'
                            if (hasActiveStreams) return 'Processing request...'
                            if (!isConnected) return 'Reconnecting...'
                            if (activeSessionId) return 'Active conversation'
                            return 'Ready to analyze operational data'
                          })()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={startNewChat}
                      disabled={activeSession?.isLoading}
                      className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-md hover:bg-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      New Conversation
                    </button>
                  </div>
                </div>

                {/* Session Error Display */}
                {activeSession?.error && (
                  <div className="p-4 bg-destructive/10 border-b">
                    <p className="text-sm text-destructive">{activeSession.error}</p>
                  </div>
                )}

                {/* Chat Messages */}
                <div ref={messagesContainerRef} className="flex-1 p-6 overflow-y-auto">                  
                  <div className="space-y-6 max-w-4xl mx-auto">
                    {/* Welcome Message */}
                    {messages.length === 0 && (
                      <div className="flex items-start space-x-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <BarChart3 className="w-5 h-5 text-primary" />
                        </div>
                        <div className="bg-muted rounded-lg p-4 max-w-2xl">
                          {user?.role === 'branch_manager' ? (
                            <>
                              <p className="text-base mb-3">
                                Hello! I'm your Operations Assistant. I can help you review your store's performance.
                              </p>

                              <div className="mb-4">
                                <p className="text-sm font-medium mb-2">Try asking things like:</p>
                                <ul className="text-sm space-y-1 text-muted-foreground">
                                  <li>• "How did my store do last week?"</li>
                                  <li>• "Compare this week vs last week"</li>
                                  <li>• "Which departments are performing best?"</li>
                                  <li>• "How's my store doing this period?"</li>
                                </ul>
                              </div>

                              <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-md">
                                <p className="text-sm text-blue-700 dark:text-blue-300">
                                  <strong>Tip:</strong> You can ask in plain language — I'll figure out the right data to pull.
                                </p>
                              </div>
                            </>
                          ) : (
                            <>
                              <p className="text-base mb-3">
                                Hello! I'm your Operations Assistant. I can help you analyze store performance data from PowerBI.
                              </p>

                              <div className="mb-4">
                                <p className="text-sm font-medium mb-2">💡 For best results, ask specific questions like:</p>
                                <ul className="text-sm space-y-1 text-muted-foreground">
                                  <li>• "Show me Period 8 2025 sales summary for all stores"</li>
                                  <li>• "Which stores performed best in Period 9 2025?"</li>
                                  <li>• "Compare week 33 2025 vs 2024 sales for Tampa"</li>
                                  <li>• "Show me weekly breakdown for top 5 stores in current period"</li>
                                </ul>
                              </div>

                              <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-md">
                                <p className="text-sm text-blue-700 dark:text-blue-300">
                                  <strong>Tip:</strong> Always specify both time periods AND years (like "Period 8 2025", "week 33 2024", or "current fiscal period") for accurate results.
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Actual Messages */}
                    {messages.map((message: OpsMessage) => (
                      <div key={message.id} className={`group flex items-start space-x-4 ${message.role === 'USER' ? 'justify-end' : ''}`}>
                        {message.role === 'ASSISTANT' && !message.isStreaming && (
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <BarChart3 className="w-5 h-5 text-primary" />
                          </div>
                        )}
                        
                        {/* Use StreamingOpsMessage for assistant messages */}
                        {message.role === 'ASSISTANT' ? (
                          <StreamingOpsMessage
                            message={message}
                            onCancelStream={handleCancelMessage}
                          />
                        ) : (
                          <div className="bg-primary text-primary-foreground rounded-lg p-4 max-w-3xl ml-auto">
                            <div className="prose prose-base max-w-full leading-relaxed">
                              <ReactMarkdown 
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={[rehypeHighlight]}
                                components={{
                                  p: ({ children }) => <p className="mb-3 last:mb-0 text-base leading-relaxed">{children}</p>,
                                  ul: ({ children }) => <ul className="mb-3 last:mb-0 ml-4 space-y-1">{children}</ul>,
                                  ol: ({ children }) => <ol className="mb-3 last:mb-0 ml-6 space-y-1 list-decimal list-outside">{children}</ol>,
                                  li: ({ children }) => <li className="text-base leading-relaxed">{children}</li>,
                                  code: ({ children, ...props }) => 
                                    <code className="bg-background/30 px-2 py-1 rounded text-sm font-mono" {...props}>
                                      {children}
                                    </code>,
                                  pre: ({ children }) => 
                                    <pre className="bg-background/30 p-4 rounded-md text-sm overflow-auto my-3 font-mono max-w-full">
                                      {children}
                                    </pre>,
                                  h1: ({ children }) => <h1 className="text-xl font-bold mb-3 mt-2">{children}</h1>,
                                  h2: ({ children }) => <h2 className="text-lg font-bold mb-2 mt-2">{children}</h2>,
                                  h3: ({ children }) => <h3 className="text-base font-semibold mb-2 mt-2">{children}</h3>,
                                  blockquote: ({ children }) => 
                                    <blockquote className="border-l-4 border-current pl-4 my-3 opacity-80 italic">
                                      {children}
                                    </blockquote>,
                                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                                  em: ({ children }) => <em className="italic">{children}</em>,
                                  hr: () => <hr className="my-4 border-current opacity-30" />,
                                  table: ({ children }) => (
                                    <div className="overflow-x-auto my-4 rounded-lg border border-primary-foreground/20 shadow-sm max-w-full">
                                      <table className="w-full table-auto">
                                        {children}
                                      </table>
                                    </div>
                                  ),
                                  thead: ({ children }) => (
                                    <thead className="bg-primary-foreground/10 border-b-2 border-primary-foreground/20">
                                      {children}
                                    </thead>
                                  ),
                                  tbody: ({ children }) => (
                                    <tbody>
                                      {children}
                                    </tbody>
                                  ),
                                  tr: ({ children }) => (
                                    <tr className="border-b border-primary-foreground/10 last:border-b-0 hover:bg-primary-foreground/10 transition-colors">
                                      {children}
                                    </tr>
                                  ),
                                  th: ({ children }) => (
                                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide border-r border-primary-foreground/20 last:border-r-0">
                                      {children}
                                    </th>
                                  ),
                                  td: ({ children }) => (
                                    <td className="px-4 py-2 text-sm border-r border-primary-foreground/20 last:border-r-0">
                                      {children}
                                    </td>
                                  )
                                }}
                              >
                                {message.content}
                              </ReactMarkdown>
                            </div>
                          </div>
                        )}

                        {message.role === 'USER' && (
                          <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-base font-medium">{user?.firstName?.[0] || 'U'}</span>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Typing Indicator */}
                    {typingUsers.length > 0 && (
                      <OpsTypingIndicator typingUsers={typingUsers} />
                    )}

                    {/* Loading Indicator - only show for non-streaming fallback */}
                    {!streamingEnabled && hasActiveStreams && (
                      <div className="flex items-start space-x-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        </div>
                        <div className="bg-muted rounded-lg p-4">
                          <p className="text-base text-muted-foreground">Analyzing data...</p>
                        </div>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {/* Scroll to Bottom Button */}
                {showScrollButton && (
                  <div className="relative">
                    <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-10">
                      <button
                        onClick={() => scrollToBottom(true)}
                        className="bg-background border border-border rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                        aria-label="Scroll to bottom"
                      >
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Chat Input */}
                <div className="border-t p-6">
                  <div className="max-w-4xl mx-auto">
                    <div className="flex space-x-3">
                      <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyPress}
                        placeholder="e.g. 'Show me Period 8 2025 sales summary for all stores' or 'Which stores performed best this fiscal period?'"
                        disabled={hasActiveStreams}
                        className="flex-1 px-4 py-3 text-base bg-background text-foreground border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 placeholder:text-muted-foreground"
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={!inputValue.trim() || hasActiveStreams}
                        className="bg-primary text-primary-foreground px-6 py-3 text-base font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {hasActiveStreams ? 'Analyzing...' : 'Send'}
                      </button>
                    </div>
                    <div className="flex items-center justify-end mt-3">
                      <span className="text-sm text-muted-foreground">Press Enter to send</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Store Memory Panel - Only for Branch Managers */}
              {user?.role === 'branch_manager' && user?.branchId && (
                <StoreMemoryPanel />
              )}

              {/* Quick Actions - Different for Branch Managers vs General Managers */}
              <div className="bg-card border rounded-lg p-4">
                <h3 className="font-semibold mb-3">Sample Queries</h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Click any example to see how to ask for specific data:
                </p>
                <div className="space-y-2">
                  {user?.role === 'branch_manager' ? (
                    <>
                      {/* Branch Manager Queries */}
                      <button
                        onClick={() => handleQuickAction('monday-review')}
                        className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          <TrendingUp className="w-4 h-4 text-blue-600" />
                          <span className="font-medium">Monday Review</span>
                        </div>
                        <div className="text-xs text-muted-foreground pl-6">
                          "How did my store do last week?"
                        </div>
                      </button>
                      <button
                        onClick={() => handleQuickAction('week-comparison')}
                        className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          <BarChart3 className="w-4 h-4 text-primary" />
                          <span className="font-medium">Weekly Comparison</span>
                        </div>
                        <div className="text-xs text-muted-foreground pl-6">
                          "Compare this week vs last week"
                        </div>
                      </button>
                      <button
                        onClick={() => handleQuickAction('department-performance')}
                        className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          <DollarSign className="w-4 h-4 text-green-600" />
                          <span className="font-medium">Department Performance</span>
                        </div>
                        <div className="text-xs text-muted-foreground pl-6">
                          "Which departments are up or down?"
                        </div>
                      </button>
                      <button
                        onClick={() => handleQuickAction('period-summary')}
                        className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          <TrendingUp className="w-4 h-4 text-primary" />
                          <span className="font-medium">Period Summary</span>
                        </div>
                        <div className="text-xs text-muted-foreground pl-6">
                          "Current fiscal period so far"
                        </div>
                      </button>
                      <button
                        onClick={() => handleQuickAction('add-note')}
                        className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          <Users className="w-4 h-4 text-orange-500" />
                          <span className="font-medium">Add a Note</span>
                        </div>
                        <div className="text-xs text-muted-foreground pl-6">
                          "Remember that we have a new assistant manager..."
                        </div>
                      </button>
                    </>
                  ) : (
                    <>
                      {/* General Manager Queries */}
                      <button
                        onClick={() => handleQuickAction('sales-period')}
                        className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          <DollarSign className="w-4 h-4 text-primary" />
                          <span className="font-medium">Period 8 {currentYear} Sales</span>
                        </div>
                        <div className="text-xs text-muted-foreground pl-6">
                          "Show me Period 8 {currentYear} sales summary for all stores"
                        </div>
                      </button>
                      <button
                        onClick={() => handleQuickAction('sales-current')}
                        className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          <DollarSign className="w-4 h-4 text-green-600" />
                          <span className="font-medium">Current Period Sales</span>
                        </div>
                        <div className="text-xs text-muted-foreground pl-6">
                          "Show me current fiscal period sales summary for all stores"
                        </div>
                      </button>
                      <button
                        onClick={() => handleQuickAction('performance')}
                        className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          <TrendingUp className="w-4 h-4 text-primary" />
                          <span className="font-medium">Top Performers</span>
                        </div>
                        <div className="text-xs text-muted-foreground pl-6">
                          "Which are our top performing stores this fiscal period?"
                        </div>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Recent Sessions */}
              <div className="bg-card border rounded-lg p-4">
                <h3 className="font-semibold mb-3">Recent Conversations</h3>
                <div className="space-y-2">
                  {loadingSessions ? (
                    <div className="text-sm text-muted-foreground">Loading...</div>
                  ) : sessions.length > 0 ? (
                    sessions.map((session: OpsChatSession) => (
                      <OpsConversationItem
                        key={session.id}
                        session={session}
                        isActive={activeSessionId === session.id}
                        isLoading={session.isLoading}
                        onSelect={loadSession}
                        onRename={handleRenameSession}
                        onDelete={handleDeleteSession}
                      />
                    ))
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      No recent conversations
                    </div>
                  )}
                </div>
              </div>

              {/* Data Sources Status */}
              <div className="bg-card border rounded-lg p-4">
                <h3 className="font-semibold mb-3">PowerBI Data Sources</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm">Sales Report Dataset</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm">Outlet Capacity Data</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm">Store Performance</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Real-time data from PowerBI dashboards
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Changelog Modal */}
        <ChangelogModal
          isOpen={showChangelog}
          onClose={() => setShowChangelog(false)}
          agentType="OPS"
        />
      </div>
    </div>
  )
}

export default OpsAgentPage